import {
  Cpu,
  Package,
  CheckCircle2,
  XCircle,
  Send,
  FileText,
} from "lucide-react";

interface QuotationType {
  name: string;
  total: number;
  won: number;
  lost: number;
  sent: number;
  draft: number;
  icon: React.ReactNode;
  gradient: string;
}

interface QuotationBreakdownProps {
  data?: {
    quotation_breakdown: {
      BuildYourPCGroup: {
        Total: number;
        Won: number;
        Lost: number;
        Draft: number;
        Sent: number;
      };
      StandaloneGroup: {
        Total: number;
        Won: number;
        Lost: number;
        Draft: number;
        Sent: number;
      };
    };
  } | null;
}

export function QuotationBreakdown({ data }: QuotationBreakdownProps) {
  const quotationTypes: QuotationType[] = [
    {
      name: "Build Your Own PC",
      total: data?.quotation_breakdown?.BuildYourPCGroup?.Total || 0,
      won: data?.quotation_breakdown?.BuildYourPCGroup?.Won || 0,
      lost: data?.quotation_breakdown?.BuildYourPCGroup?.Lost || 0,
      sent: data?.quotation_breakdown?.BuildYourPCGroup?.Sent || 0,
      draft: data?.quotation_breakdown?.BuildYourPCGroup?.Draft || 0,
      icon: <Cpu size={20} className="text-white" />,
      gradient: "gradient-blue",
    },
    {
      name: "Standalone Products",
      total: data?.quotation_breakdown?.StandaloneGroup?.Total || 0,
      won: data?.quotation_breakdown?.StandaloneGroup?.Won || 0,
      lost: data?.quotation_breakdown?.StandaloneGroup?.Lost || 0,
      sent: data?.quotation_breakdown?.StandaloneGroup?.Sent || 0,
      draft: data?.quotation_breakdown?.StandaloneGroup?.Draft || 0,
      icon: <Package size={20} className="text-white" />,
      gradient: "gradient-purple",
    },
  ];

  const totalQuotations = quotationTypes.reduce(
    (sum, type) => sum + type.total,
    0
  );
  const totalWon = quotationTypes.reduce((sum, type) => sum + type.won, 0);
  const winRate =
    totalQuotations > 0
      ? ((totalWon / totalQuotations) * 100).toFixed(1)
      : "0.0";

  // const getGradientClass = (gradient: string) => {
  //   switch (gradient) {
  //     case "gradient-blue":
  //       return "bg-primary";
  //     case "gradient-purple":
  //       return "bg-info";
  //     default:
  //       return "bg-primary";
  //   }
  // };

  return (
    <div className="card h-100 mb-0">
      <div className="card-body py-3 mb-0">
        <h5 className="card-title mb-2">Quotation Breakdown</h5>

        {/* Summary Stats */}
        {/* <div className="bg-primary-subtle p-4 rounded mb-4">
          <div className="row align-items-center">
            <div className="col-md-6">
              <p className="text-muted small mb-1">Total Quotations</p>
              <h2 className="text-primary mb-0">{totalQuotations}</h2>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="d-flex align-items-center justify-content-md-end gap-1">
                <div
                  className="bg-success rounded-circle"
                  style={{ width: "8px", height: "8px" }}
                ></div>
                <span className="text-muted small">Win Rate: {winRate}%</span>
              </div>
            </div>
          </div> */}
        {/* </div> */}

        {/* Horizontal Layout for Quotation Types */}
        <div className="row g-4">
          {quotationTypes.map((type, index) => {
            const wonPercentage =
              type.total === 0 ? 0 : (type.won / type.total) * 100;
            const lostPercentage =
              type.total === 0 ? 0 : (type.lost / type.total) * 100;
            const sentPercentage =
              type.total === 0
                ? 0
                : ((type.sent + type.won + type.lost) / type.total) * 100;
            const draftPercentage =
              type.total === 0 ? 0 : (type.draft / type.total) * 100;

            return (
              <div key={type.name} className="col-lg-6">
                <div className="border rounded p-3 bg-white h-100">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className={`bg-primary p-2 rounded`}>{type.icon}</div>
                    <div className="flex-fill">
                      <h6 className="mb-1">{type.name}</h6>
                      <p className="text-muted small mb-0">
                        Total: {type.total} quotations
                      </p>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <CheckCircle2 size={16} className="text-success" />
                          <span className="text-muted small">Won</span>
                        </div>
                        <span className="text-success fw-semibold">
                          {type.won} ({wonPercentage.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{ width: `${wonPercentage}%` }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <XCircle size={16} className="text-danger" />
                          <span className="text-muted small">Lost</span>
                        </div>
                        <span className="text-danger fw-semibold">
                          {type.lost} ({lostPercentage.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar bg-danger"
                          role="progressbar"
                          style={{ width: `${lostPercentage}%` }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <Send size={16} className="text-warning" />
                          <span className="text-muted small">Sent</span>
                        </div>
                        <span className="text-warning fw-semibold">
                          {type.sent + type.won + type.lost} (
                          {sentPercentage.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar bg-warning"
                          role="progressbar"
                          style={{ width: `${sentPercentage}%` }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <FileText size={16} className="text-primary" />
                          <span className="text-muted small">Draft</span>
                        </div>
                        <span className="text-primary fw-semibold">
                          {type.draft} ({draftPercentage.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar bg-primary"
                          role="progressbar"
                          style={{ width: `${draftPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
